import { useState, useEffect, useRef, useCallback } from "react";

const COOLDOWN_MS = 10 * 60 * 1000;

// Feb 2026, user's explicit ask ("add a restriction 10 min gap. if
// user click one time then wait 10 min for reuse this button") -
// shared by ActAsSupplierPage and SupplierDashboardPage's "Pull Latest
// POs" buttons. The backend (server.py's _claim_po_manual_refresh_
// cooldown, keyed by vendor_code) is the real source of truth and
// rejects an early click with HTTP 429 regardless of what this hook
// shows - this is purely so the button visibly disables/counts down
// instead of the user hitting a surprise error toast. Persisted to
// localStorage (keyed by vendor_code) so a page refresh mid-cooldown
// doesn't make the button look clickable again.
export function usePullCooldown(storageKeyPrefix, vendorCode) {
  const storageKey = vendorCode ? `${storageKeyPrefix}:${vendorCode}` : null;
  const [remainingMs, setRemainingMs] = useState(0);
  const intervalRef = useRef(null);

  const tick = useCallback(() => {
    if (!storageKey) {
      setRemainingMs(0);
      return;
    }
    const until = Number(localStorage.getItem(storageKey) || 0);
    setRemainingMs(Math.max(until - Date.now(), 0));
  }, [storageKey]);

  useEffect(() => {
    tick();
    intervalRef.current = setInterval(tick, 1000);
    return () => clearInterval(intervalRef.current);
  }, [tick]);

  const startCooldown = useCallback((minutes) => {
    if (!storageKey) return;
    const ms = minutes != null ? minutes * 60 * 1000 : COOLDOWN_MS;
    localStorage.setItem(storageKey, String(Date.now() + ms));
    tick();
  }, [storageKey, tick]);

  const remainingLabel = remainingMs > 0 ? `Wait ${Math.ceil(remainingMs / 60000)} min` : null;

  return { onCooldown: remainingMs > 0, remainingLabel, startCooldown };
}
